import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";
import { toast } from "react-toastify";
import ConfirmDialog from "./listing/ConfirmDialog";
import useCallCenterT from "../../hooks/useCallCenterT";
import {
  deleteTicketAttachment,
  downloadTicketAttachment,
  listTicketAttachments,
  uploadTicketAttachment,
} from "../../service/CallCenterTickets";

const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString();
};

const TicketAttachments = ({ ticketId, refreshKey = 0 }) => {
  const t = useCallCenterT();
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  const loadAttachments = useCallback(async () => {
    if (!ticketId) {
      setAttachments([]);
      return;
    }
    try {
      setLoading(true);
      const response = await listTicketAttachments(ticketId);
      setAttachments(Array.isArray(response) ? response : []);
    } catch (error) {
      toast.error(error?.message || t("ccTicketAttachmentsLoadFailed"));
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  }, [ticketId, t]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments, refreshKey]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !ticketId) return;
    try {
      setUploading(true);
      await uploadTicketAttachment(ticketId, file);
      toast.success(t("ccTicketAttachmentUploaded"));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      await loadAttachments();
    } catch (error) {
      toast.error(error?.message || t("ccTicketAttachmentUploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (attachment) => {
    if (!attachment?.id || !ticketId) return;
    try {
      setDownloadingId(attachment.id);
      await downloadTicketAttachment(ticketId, attachment.id, attachment.fileName || "attachment");
    } catch (error) {
      toast.error(error?.message || t("ccTicketAttachmentDownloadFailed"));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteRequest = (attachment) => {
    if (!attachment?.id || !ticketId) return;
    setDeleteTarget(attachment);
  };

  const handleDeleteCancel = () => {
    if (deletingId) return;
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id || !ticketId) return;
    try {
      setDeletingId(deleteTarget.id);
      await deleteTicketAttachment(ticketId, deleteTarget.id);
      toast.success(t("ccTicketAttachmentDeleted"));
      setDeleteTarget(null);
      await loadAttachments();
    } catch (error) {
      toast.error(error?.message || t("ccTicketAttachmentDeleteFailed"));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">{t("ccTicketAttachmentsHint")}</p>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleUpload}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !ticketId}
            className="h-9 px-4 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {uploading ? t("ccProcessing") : t("ccTicketAttachFile")}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">{t("ccLoading")}</p>
      ) : !attachments.length ? (
        <p className="text-sm text-gray-500">{t("ccTicketAttachmentsEmpty")}</p>
      ) : (
        <div className="space-y-2">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-800 truncate">{attachment.fileName}</div>
                <div className="text-xs text-gray-500">
                  {formatFileSize(attachment.fileSize)}
                  {attachment.uploadedByName ? ` · ${attachment.uploadedByName}` : ""}
                  {attachment.createdAt ? ` · ${formatDateTime(attachment.createdAt)}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleDownload(attachment)}
                  disabled={downloadingId === attachment.id || deletingId === attachment.id}
                  title={t("ccTicketAttachmentDownload")}
                  className="h-8 px-3 rounded-lg bg-[#236DB4] text-xs font-semibold text-white hover:bg-[#1d5a96] disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <FiDownload size={14} aria-hidden="true" />
                  {downloadingId === attachment.id ? t("ccProcessing") : t("ccTicketAttachmentDownload")}
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteRequest(attachment)}
                  disabled={deletingId === attachment.id || downloadingId === attachment.id}
                  title={t("ccDelete")}
                  className="h-8 px-3 rounded-lg border border-red-200 bg-white text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <MdOutlineDelete size={16} aria-hidden="true" />
                  {deletingId === attachment.id ? t("ccProcessing") : t("ccDelete")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={t("ccConfirmDialogTitle")}
        message={t("ccConfirmDeleteAttachment")}
        cancelLabel={t("ccCancel")}
        confirmLabel={t("ccConfirmDialogProceed")}
        processingLabel={t("ccProcessing")}
        variant="danger"
        loading={Boolean(deletingId)}
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
        overlayClassName="z-[60]"
      />
    </div>
  );
};

export default TicketAttachments;
